import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Users, Ticket, Camera } from 'lucide-react';
import { API_BASE_URL } from './apiConfig';
import { istDateMed, istTime } from './utils/istDate';

const EventsList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/events`).then(r=>r.json()).then(res=>{
      if (res.success) setEvents(res.events || []);
    }).finally(()=>setLoading(false));
  }, []);

  const formatEventDate = (dateString) => ({
    date: istDateMed(dateString), // forced IST
    time: istTime(dateString),
  });

  return (
    <div className="min-h-screen" style={{background:'#F4ECDE'}}>
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/10"></div>
      
      {/* Simplified Header for Embedding */}
      <div className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                navigate('/');
              }}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{events.length} Event{events.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{borderColor: '#471F21'}}></div>
              <div className="text-gray-600">Loading events...</div>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Events Currently</h3>
            <p className="text-gray-600 mb-6">Check back later for exciting events at Thaathayya Illu!</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 rounded-lg text-white font-medium transition-colors"
              style={{backgroundColor: '#471F21'}}
            >
              Back to Home
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {events.map(evt => {
              const eventDate = formatEventDate(evt.start_at);
              return (
                <Link 
                  to={`/events/${evt.id}`} 
                  key={evt.id} 
                  className="group bg-white rounded-xl shadow hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-200 hover:border-gray-300 block"
                >
                  {/* Event Image */}
                  <div className="relative h-40 overflow-hidden">
                    {evt.cover_image_url ? (
                      <img 
                        src={evt.cover_image_url} 
                        alt={evt.name} 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <Camera className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                    {evt.status === 'published' && (
                      <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded-full text-xs font-semibold" style={{color: '#471F21'}}>
                        Live
                      </div>
                    )}
                  </div>

                  {/* Event Content */}
                  <div className="p-4">
                    <h3 className="text-lg font-bold mb-2 line-clamp-1" style={{color:'#471F21'}}>
                      {evt.name}
                    </h3>
                    
                    <div className="space-y-1 mb-3">
                      <div className="flex items-center text-xs text-gray-600">
                        <Calendar className="w-3 h-3 mr-2" />
                        <span>{eventDate.date}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-600">
                        <Clock className="w-3 h-3 mr-2" />
                        <span>{eventDate.time}</span>
                      </div>
                    </div>

                    {evt.description && (
                      <p className="text-gray-600 text-xs mb-3 line-clamp-2">
                        {evt.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-500 flex items-center">
                        <Ticket className="w-3 h-3 mr-1" />
                        View Details
                      </span>
                      <span className="text-xs font-bold" style={{color: '#471F21'}}>→</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsList;


